import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { DairyAddonService } from './dairy-addon.service';

/**
 * PLAN_CONFIG — Client-side mirror of m_v1/helpers/plan_config.php.
 *
 * IMPORTANT: This is for UX gating only (hiding/disabling UI elements).
 * All security enforcement is done server-side in PHP.
 * Keep in sync with plan_config.php when adding new plans or features.
 */
export const PLAN_LEVELS: Record<string, number> = {
    STARTER: 1,
    BASIC:   1,
    ADVANCE: 2,
    PREMIUM: 3,
};

export interface PlanFeatures {
    web_access:      boolean;
    ledger:          boolean;
    daybook:         boolean;
    multi_dairy:     boolean;
    reports:         boolean;
    dairy_users:     boolean;
    market_purchase: boolean;  // MARKET rate milk purchase (Advance+)
    milk_stock:      boolean;  // Milk Stock report (Premium only)
    route_report:    boolean;  // Route Report (Advance+)
    trial_balance:   boolean;  // Trial Balance (Advance+)
    milk_analysis:   boolean;  // Milk Chemical Analysis (Premium only)
    milk_dispatch:   boolean;  // Milk Dispatch Voucher (Premium only)
    storage_locations: boolean;
    production:      boolean;  // Production workflow (Premium only)
    chilling_bill:   boolean;  // Chilling Bill (Premium only)
    head_load:       boolean;  // Head Load billing (Premium only)
    profit_loss:     boolean;  // Profit & Loss report (Premium only)
    balance_sheet:   boolean;  // Balance Sheet report (Premium only)
    member_portal:   boolean;  // Member Portal toggle (Advance+)
    gst_billing:     boolean;  // GST billing — HSN code + tax on products (Premium only, SA-toggled)
}

export interface PlanConfig {
    max_dairies:     number;
    allowed_devices: string[];
    features:        PlanFeatures;
}

const PLAN_CONFIGS: Record<string, PlanConfig> = {
    STARTER: {
        max_dairies:     1,
        allowed_devices: ['ANDROID'],
        features: { web_access: false, ledger: false, daybook: false, multi_dairy: false, reports: false, dairy_users: false, market_purchase: false, milk_stock: false, route_report: false, trial_balance: false, milk_analysis: false, milk_dispatch: false, production: false, storage_locations: false, chilling_bill: false, head_load: false, profit_loss: false, balance_sheet: false, member_portal: false, gst_billing: false },
    },
    BASIC: {
        max_dairies:     1,
        allowed_devices: ['ANDROID'],
        features: { web_access: false, ledger: true, daybook: true, multi_dairy: false, reports: false, dairy_users: false, market_purchase: false, milk_stock: false, route_report: false, trial_balance: false, milk_analysis: false, milk_dispatch: false, production: false, storage_locations: false, chilling_bill: false, head_load: false, profit_loss: false, balance_sheet: false, member_portal: false, gst_billing: false },
    },
    ADVANCE: {
        max_dairies:     1,
        allowed_devices: ['ANDROID', 'WEB_AND_ANDROID', 'ALL_DEVICES'],
        features: { web_access: true, ledger: true, daybook: true, multi_dairy: false, reports: true, dairy_users: true, market_purchase: true, milk_stock: false, route_report: true, trial_balance: true, milk_analysis: false, milk_dispatch: false, production: false, storage_locations: false, chilling_bill: false, head_load: false, profit_loss: false, balance_sheet: false, member_portal: true, gst_billing: false },
    },
    PREMIUM: {
        max_dairies:     Number.POSITIVE_INFINITY,
        allowed_devices: ['ANDROID', 'WEB_AND_ANDROID', 'ALL_DEVICES'],
        features: { web_access: true, ledger: true, daybook: true, multi_dairy: true, reports: true, dairy_users: true, market_purchase: true, milk_stock: true, route_report: true, trial_balance: true, milk_analysis: true, milk_dispatch: true, production: true, storage_locations: true, chilling_bill: true, head_load: true, profit_loss: true, balance_sheet: true, member_portal: true, gst_billing: true },
    },
};

@Injectable({ providedIn: 'root' })
export class PlanService {

    constructor(private authService: AuthService, private addons: DairyAddonService) {}

    /** Returns the normalised plan type string (always uppercase). */
    getPlanType(): string {
        const dairy = this.authService.getCurrentDairy();
        return (dairy?.plan_soft || 'BASIC').toUpperCase();
    }

    getAccountPlanType(): string {
        const user = this.authService.getCurrentUser();
        return (user?.user_plan_type || 'BASIC').toUpperCase();
    }

    /** Returns the app_user_type string (always uppercase). */
    getDeviceType(): string {
        const user = this.authService.getCurrentUser();
        return (user?.app_user_type || 'ANDROID').toUpperCase();
    }

    /** Returns 1 (BASIC), 2 (ADVANCE), or 3 (PREMIUM). */
    getPlanLevel(): number {
        return PLAN_LEVELS[this.getPlanType()] ?? 1;
    }

    /** Returns the full config object for the user's current plan. */
    getPlanConfig(): PlanConfig {
        const plan = this.getPlanType();
        return PLAN_CONFIGS[plan] ?? PLAN_CONFIGS['BASIC'];
    }

    /**
     * Whether a specific feature key is enabled on the user's plan.
     * Feature keys: 'web_access' | 'ledger' | 'daybook' | 'multi_dairy' | 'reports'
     */
    isFeatureAllowed(feature: keyof PlanFeatures): boolean {
        if (this.isRenewalOnlyAccess()) return false;
        if (feature === 'production' || feature === 'storage_locations') {
            return this.isPremium && this.addons.active('PRODUCTION_STORAGE');
        }
        return this.getPlanConfig().features[feature] ?? false;
    }

    isRenewalOnlyAccess(): boolean {
        const dairy = this.authService.getCurrentDairy();
        if (!dairy) return false;
        const plan = String(dairy?.plan_soft || 'BASIC').toUpperCase();
        if (plan === 'BASIC' || plan === 'STARTER') return true;
        if (this.isDateActive(dairy?.access_grace_until)) return false;
        const status = String(dairy?.subscription_status || 'ACTIVE').toUpperCase();
        return status !== 'ACTIVE' || !this.isDateActive(dairy?.amc_date || dairy?.expiry_date);
    }

    /**
     * Whether the user can create another dairy based on their plan limit
     * and how many dairies are already in their logged-in user data.
     */
    canCreateDairy(): boolean {
        const user = this.authService.getCurrentUser();
        const currentCount = (user?.dairies?.length ?? 0);
        const max = this.getMaxDairies();
        return currentCount < max;
    }

    /** Max dairies allowed on the user's current plan. */
    getMaxDairies(): number {
        const user = this.authService.getCurrentUser();
        if (user && Object.prototype.hasOwnProperty.call(user, 'max_dairies')) {
            return user.max_dairies === null ? Number.POSITIVE_INFINITY : Number(user.max_dairies);
        }
        return this.getAccountPlanConfig().max_dairies;
    }

    /** Count of dairies currently linked to the user (from login data). */
    getCurrentDairyCount(): number {
        const user = this.authService.getCurrentUser();
        return user?.dairies?.length ?? 0;
    }

    /**
     * Returns true if the user is on BASIC plan AND the device type is ANDROID.
     * Used to show web-access restriction banners.
     */
    isWebRestricted(): boolean {
        return !this.isFeatureAllowed('web_access');
    }

    get isBasic():   boolean { return this.getPlanLevel() === 1; }
    get isAdvance(): boolean { return this.getPlanLevel() >= 2; }
    get isPremium(): boolean { return this.getPlanLevel() >= 3; }

    /**
     * GST billing is enabled only when:
     *   1. The user is on a Premium plan (plan-level gate), AND
     *   2. The super-admin has toggled gst_enabled = 1 for this dairy.
     *
     * This is intentionally a runtime check so SA can enable/disable
     * without requiring a new deploy.
     */
    isGstEnabled(): boolean {
        if (!this.isFeatureAllowed('gst_billing')) return false;
        return !!this.authService.getCurrentDairy()?.gst_enabled;
    }

    private getAccountPlanConfig(): PlanConfig {
        return PLAN_CONFIGS[this.getAccountPlanType()] ?? PLAN_CONFIGS['BASIC'];
    }

    private isDateActive(rawDate: unknown): boolean {
        const value = String(rawDate || '').trim();
        if (!value) return false;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return false;
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) date.setHours(23, 59, 59, 999);
        return date.getTime() >= Date.now();
    }
}
